import axios from "axios";
import { z } from "zod";
import { environment } from "../../../environment";
import { errorLog } from "../../../logger";
import pipe from "../../../utils/pipe";
import { ClientId, ExternalAccountId, FartherAccountId, FundingAccountStatusOptions, FundingMovementTypeOptions, InstitutionId, PershingStandingInstructionFundingStatusOptions, PhysicalAccountId, PlanId, USAStates, } from "../../Types";
import ValidationError from "../../ZodError";
const LinkStatus = z.enum(["Valid", "Expired", "Error"]);
const ConnectionHealthType = z.enum([
    "Healthy",
    "Unavailable",
    "Degraded",
    "Down",
]);
export const AccountType = z.enum([
    "credit",
    "depository",
    "investment",
    "loan",
    "Savings",
    "Checking",
]);
export const CurrencyCode = z.enum(["USD", "CAD"]);
const Bank = z.object({
    bankId: z.string(),
    routingNumber: z.string(),
    name: z.string(),
    city: z.string(),
    state: z.enum(USAStates),
});
const ManualDetails = z.object({
    account: z.object({
        accountType: AccountType,
        accountTitle: z.string().optional(),
        accountNumber: z.string().nullable(),
        accountHolderNames: z.string().array().optional(),
    }),
    bank: Bank.optional(),
});
const Link = z.object({
    linkId: z.string(),
    itemId: z.string(),
    accessToken: z.string(),
    status: LinkStatus,
});
const Institution = z.object({
    institutionId: InstitutionId,
    name: z.string(),
});
export const PlaidAccountTypeSchema = z.enum([
    "credit",
    "depository",
    "investment",
    "loan",
    "Savings",
    "Checking",
]);
const PlaidAccountDetails = z.object({
    connectionHealthType: ConnectionHealthType,
    accountType: AccountType,
    accountSubtype: z.string(),
    plaidAccountId: z.string(),
    name: z.string(),
    officialName: z.string().nullable(),
    accountNumberMask: z.string().nullable(),
    currencyCode: CurrencyCode,
    availableBalance: z.number().nullable(),
    currentBalance: z.number().nullable(),
    balanceLimit: z.number().nullable(),
    accountNumber: z.string().nullable(),
    routingNumber: z.string().nullable(),
    wireNumber: z.string().nullable(),
    accountHolders: z.string().array().nullable(),
    processorToken: z.string().nullable(),
    isVisible: z.boolean(),
    isValidForFunding: z.boolean(),
});
const PlaidDetails = z.object({
    account: PlaidAccountDetails,
    link: Link,
    institution: Institution.optional(),
});
const ApexACHRelationshipStatus = z.enum([
    "APPROVED",
    "REJECTED",
    "CANCELED",
    "PENDING",
]);
const StandingInstructionResource = z.object({
    instructionId: z.string().optional(),
    virtualAccountId: FartherAccountId,
    physicalAccountId: PhysicalAccountId,
    externalAccountId: ExternalAccountId,
    enterpriseId: z.string().optional(),
    sequenceNumber: z.string().optional(),
    status: z.enum(PershingStandingInstructionFundingStatusOptions),
    movementType: z.enum(FundingMovementTypeOptions).optional(),
});
const ACHRelationshipResource = z.object({
    relationshipId: z.string(),
    virtualAccountId: FartherAccountId,
    physicalAccountId: PhysicalAccountId,
    externalAccountId: ExternalAccountId,
    relationshipIdValue: z.string(),
    status: ApexACHRelationshipStatus,
});
const FundingDetailsManual = z.object({
    fundingStatus: z.enum(FundingAccountStatusOptions),
    planId: PlanId.nullable(),
    fundingResources: StandingInstructionResource.array(),
    validAchAccountIds: FartherAccountId.array(),
});
const FundingDetailsPlaid = z.object({
    fundingStatus: z.enum(FundingAccountStatusOptions),
    planId: PlanId.nullable(),
    fundingResources: z
        .union([ACHRelationshipResource, StandingInstructionResource])
        .array(),
    validAchAccountIds: FartherAccountId.array(),
});
const PlaidAccount = z.object({
    accountId: ExternalAccountId,
    accountProvider: z.literal("Plaid"),
    clientId: ClientId,
    details: PlaidDetails.nullable(),
    fundingDetails: FundingDetailsPlaid.nullable(),
});
const ManualAccount = z.object({
    accountId: ExternalAccountId,
    accountProvider: z.literal("Manual"),
    clientId: ClientId,
    details: ManualDetails.nullable(),
    fundingDetails: FundingDetailsManual.nullable(),
});
const FundingAccount = z.discriminatedUnion("accountProvider", [
    PlaidAccount,
    ManualAccount,
]);
const Account = z.discriminatedUnion("accountProvider", [
    PlaidAccount,
    ManualAccount,
]);
const Response = z.object({
    fundingAccount: FundingAccount.nullable(),
    accounts: Account.array(),
});
export const url = () => environment() === "PROD"
    ? `https://middleware.prod.farther.com/account/v1/client/external-accounts`
    : `https://middleware.uat.farther.com/account/v1/client/external-accounts`;
export default async function clientFundingAccounts(config) {
    const { data } = await axios.get(url(), {
        headers: {
            Authorization: `Bearer ${config.jwt}`,
            Client: config.clientId,
            ...(config.advisorId && { Advisor: config.advisorId }),
        },
    });
    const decoded = Response.safeParse(data);
    if (decoded.success) {
        const fa = decoded.data.fundingAccount;
        const extendedFA = fa === null
            ? null
            : fa.accountProvider === "Manual"
                ? fa
                : extendPlaidAccount(fa);
        return {
            ...decoded.data,
            fundingAccount: extendedFA,
            accounts: decoded.data.accounts.map((a) => a.accountProvider === "Manual" ? a : extendPlaidAccount(a)),
        };
    }
    else {
        const errorMessage = "[clientFundingAccounts] Could not parse";
        errorLog(errorMessage, decoded.error.message);
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
}
function extendPlaidAccount(a) {
    return {
        ...a,
        details: pipe(a.details, (d) => ({
            ...d,
            canBeSetAsFundingAccount: d.link.status === "Valid" && d.account.isValidForFunding === true,
        })),
    };
}
//# sourceMappingURL=getClientFundingAccounts.js.map